// src/services/bookingService.ts
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Package } from '../types/Venue';
import { User } from '../contexts/UserContext';

// Booking status types
export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'refunded';

// Package booking interface
export interface PackageBooking {
  id: string;
  packageId: string;
  packageName: string;
  venueId: string;
  venueName: string;
  userId: string;
  userEmail: string;
  userName: string;
  bookingDate: Timestamp | null;
  timeSlot: {
    start: string;
    end: string;
  };
  guests: number;
  amount: number;
  status: BookingStatus;
  paymentId?: string;
  specialRequests?: string;
  contactPhone?: string;
  redemptionCode: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  redeemedAt?: Timestamp;
  cancelledAt?: Timestamp;
}

/**
 * Service for handling package bookings
 */
class BookingService {
  /**
   * Creates a booking for a package
   */
  async createBooking(
    venueId: string,
    packageId: string,
    packageData: Package,
    user: User,
    bookingDetails: {
      date: string;
      timeSlot: string;
      guests: number;
      specialRequests?: string;
      phone?: string;
    },
    paymentId?: string
  ): Promise<string> {
    try {
      // Get the venue details
      const venueDoc = await getDoc(doc(db, 'venues', venueId));
      
      if (!venueDoc.exists()) {
        throw new Error('Venue not found');
      }
      
      const venueData = venueDoc.data();
      
      // Parse the selected time slot
      const [startTime, endTime] = bookingDetails.timeSlot.split(' - ');
      
      // Generate a unique redemption code
      const redemptionCode = this.generateRedemptionCode();
      
      // Create the booking record
      const bookingData: Omit<PackageBooking, 'id'> = {
        packageId,
        packageName: packageData.name,
        venueId,
        venueName: venueData.name,
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.displayName || '',
        bookingDate: Timestamp.fromDate(new Date(bookingDetails.date)),
        timeSlot: {
          start: startTime,
          end: endTime
        },
        guests: bookingDetails.guests,
        amount: packageData.price * 1.08, // Price with tax
        status: paymentId ? 'confirmed' : 'pending',
        paymentId,
        specialRequests: bookingDetails.specialRequests,
        contactPhone: bookingDetails.phone,
        redemptionCode,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp
      };
      
      // Add the booking to Firestore
      const bookingRef = await addDoc(collection(db, 'packageBookings'), bookingData);
      
      return bookingRef.id;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  }
  
  /**
   * Updates a booking status
   */
  async updateBookingStatus(bookingId: string, status: BookingStatus): Promise<void> {
    try {
      await updateDoc(doc(db, 'packageBookings', bookingId), {
        status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }
  
  /**
   * Gets a booking by ID
   */
  async getBookingById(bookingId: string): Promise<PackageBooking | null> {
    try {
      const bookingDoc = await getDoc(doc(db, 'packageBookings', bookingId));
      
      if (!bookingDoc.exists()) {
        return null;
      }
      
      return {
        id: bookingDoc.id,
        ...bookingDoc.data()
      } as PackageBooking;
    } catch (error) {
      console.error('Error getting booking:', error);
      throw error;
    }
  }
  
  /**
   * Gets all bookings for a specific venue
   */
  async getVenueBookings(venueId: string): Promise<PackageBooking[]> {
    try {
      const bookingsQuery = query(
        collection(db, 'packageBookings'),
        where('venueId', '==', venueId),
        orderBy('createdAt', 'desc')
      );

      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings: PackageBooking[] = [];

      bookingsSnapshot.forEach((doc) => {
        bookings.push({
          id: doc.id,
          ...doc.data()
        } as PackageBooking);
      });

      return bookings;
    } catch (error) {
      console.error('Error getting venue bookings:', error);
      throw error;
    }
  }

  /**
   * Gets all bookings for a specific venue (alias for getVenueBookings)
   */
  async getBookingsByVenueId(venueId: string): Promise<PackageBooking[]> {
    return this.getVenueBookings(venueId);
  }

  /**
   * Gets all bookings for a specific user
   */
  async getUserBookings(userId: string): Promise<PackageBooking[]> {
    try {
      const bookingsQuery = query(
        collection(db, 'packageBookings'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookings: PackageBooking[] = [];

      bookingsSnapshot.forEach((doc) => {
        bookings.push({
          id: doc.id,
          ...doc.data()
        } as PackageBooking);
      });

      return bookings;
    } catch (error) {
      console.error('Error getting user bookings:', error);
      throw error;
    }
  }

  /**
   * Verifies a redemption code for a venue
   */
  async verifyRedemptionCode(venueId: string, code: string): Promise<{
    valid: boolean;
    booking?: PackageBooking;
  }> {
    try {
      const bookingsQuery = query(
        collection(db, 'packageBookings'),
        where('venueId', '==', venueId),
        where('redemptionCode', '==', code)
      );

      const bookingsSnapshot = await getDocs(bookingsQuery);
      
      if (bookingsSnapshot.empty) {
        return { valid: false };
      }

      const bookingDoc = bookingsSnapshot.docs[0];
      const booking = {
        id: bookingDoc.id,
        ...bookingDoc.data()
      } as PackageBooking;

      // Check if booking is already redeemed or cancelled
      if (booking.status === 'completed') {
        return { 
          valid: false, 
          booking
        };
      }

      if (booking.status === 'cancelled' || booking.status === 'refunded') {
        return { 
          valid: false, 
          booking
        };
      }

      // Check if booking date is today or in the past
      const bookingDate = booking.bookingDate?.toDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (!bookingDate || bookingDate > today) {
        return { 
          valid: false, 
          booking
        };
      }

      return { 
        valid: true, 
        booking 
      };
    } catch (error) {
      console.error('Error verifying redemption code:', error);
      throw error;
    }
  }

  /**
   * Marks a booking as redeemed/completed
   */
  async redeemBooking(bookingId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'packageBookings', bookingId), {
        status: 'completed',
        redeemedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error redeeming booking:', error);
      throw error;
    }
  }

  /**
   * Cancels a booking
   */
  async cancelBooking(bookingId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'packageBookings', bookingId), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  }

  /**
   * Generates a unique redemption code
   */
  private generateRedemptionCode(): string {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluded similar looking characters like I, 1, O, 0
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }
}

export default new BookingService();
